import { useEffect, useMemo, useState } from 'react';
import { useData, iconToken, iconUrl } from './dataStore';
import { collectRefs } from './schema';
import type { DataIconRecord, DataRecord, RefValue } from './types';

/** guid → record (records created on the web have no guid until they exist in Unity). */
export function useRecordsByGuid(): Record<string, DataRecord> {
  const records = useData((s) => s.records);
  return useMemo(() => {
    const out: Record<string, DataRecord> = {};
    for (const r of Object.values(records)) if (r.guid) out[r.guid] = r;
    return out;
  }, [records]);
}

/** The record a reference points at: {guid} → by asset guid, {new: id} → a web-created record by id. */
export function useRefTarget(value: RefValue | null | undefined): DataRecord | undefined {
  const byGuid = useRecordsByGuid();
  const records = useData((s) => s.records);
  if (!value) return undefined;
  if (value.guid) return byGuid[value.guid];
  if (value.new) return records[value.new];
  return undefined;
}

/** How other records reference this one: its asset guid, or "new:<id>" while it only exists on the web. */
export function refKeyOf(r: DataRecord): string {
  return r.guid || 'new:' + r.id;
}

/** guid (or "new:<id>") → the records (and top-level fields) that reference it: the "Used by" list. */
export function useReverseRefs(): Record<string, { id: string; field: string }[]> {
  const records = useData((s) => s.records);
  return useMemo(() => {
    const out: Record<string, { id: string; field: string }[]> = {};
    for (const r of Object.values(records)) {
      const seen = new Set<string>();
      for (const ref of collectRefs(r.values)) {
        const k = ref.guid + '|' + ref.field;
        if (seen.has(k)) continue;
        seen.add(k);
        (out[ref.guid] ??= []).push({ id: r.id, field: ref.field });
      }
    }
    return out;
  }, [records]);
}

/** URL of a sprite icon (protected file → short-lived token), or null while loading / missing. */
export function useIconSrc(key: string | undefined | null): string | null {
  const icon = useData((s) => (key ? s.icons[key] : undefined));
  const [loaded, setLoaded] = useState<{ icon: DataIconRecord; src: string } | null>(null);
  useEffect(() => {
    if (!icon) return;
    let alive = true;
    void iconToken()
      .then((token) => alive && setLoaded({ icon, src: iconUrl(icon, token) }))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [icon]);
  // A result for another (or a replaced) icon is stale.
  return icon && loaded?.icon === icon ? loaded.src : null;
}
